import * as React from 'react';
import styled from 'styled-components';
import { PageContentImage, Query, QueryGetPageContentInputArgs, Maybe } from '../../../../client/types/generated/types';

import { useInterval } from '../../../utils/hooks/useInterval';
import { H1 } from '../HeaderTitle';

const Header = styled.div``;

//GrapQL
import { getAdminClient } from '../admin/AdminApolloClient';
import { useQuery } from '@apollo/client';
import { PageContentType } from '../../../../server/global/enum';
import { CONTENT_ITEM } from '../../../../client/__graphql__/queries/page_content/getPageContent';
import { isExternalURL, IsValidUrl } from '../../../utils/helpers/url';
import { LogoNybygg } from '../../ui/logo/LogoNybygg';

type GetPageItem = Pick<Query, 'getNdPageContent'>;
type GetPageItemVariables = QueryGetPageContentInputArgs;

interface ImageItem {
  id: number;
  loaded: boolean;
  url: string;
  title?: string;
  description?: string;
  location?: string;
  property_id?: number;
  linkTo?: string;
}

export const HeaderTop: React.FC = () => {
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [images, setImages] = React.useState<ImageItem[]>(new Array<ImageItem>());
  const [className, setClassName] = React.useState('');

  const lazyLoad = (index: number) => {
    setImages((prev) => {
      return prev.map((item, imageIndex) => {
        if (index === imageIndex || index + 1 === imageIndex) {
          return { ...item, loaded: true };
        } else {
          return item;
        }
      });
    });
  };

  const onClick = () => {
    if (images[currentImageIndex]?.linkTo && IsValidUrl(images[currentImageIndex]?.linkTo)) {
      window.open(images[currentImageIndex]?.linkTo, '_blank');
    }
  };

  const { data, loading } = useQuery<GetPageItem, GetPageItemVariables>(CONTENT_ITEM, {
    variables: { input: { urltag: '', type: PageContentType.HOME } },
    fetchPolicy: 'network-only',
    onCompleted: (data: GetPageItem) => {
      if (data?.getNdPageContent?.imageList && data?.getNdPageContent?.imageList?.length > 0) {
        const array: Array<ImageItem> = [];

        data?.getNdPageContent?.imageList?.map((item: Maybe<PageContentImage>, index: number) => {
          if (item && item?.url) {
            array.push({
              id: index + 1,
              loaded: false,
              url: isExternalURL(item.url).link,
              title: item?.title ? item?.title : undefined,
              description: item?.description ? item?.description : undefined,
              linkTo: item?.linkTo ? item?.linkTo : undefined
            });
          }
        });

        setImages(array);

        if (array.length > 0) {
          const cn = IsValidUrl(array[0]?.linkTo) ? 'click' : '';
          if (cn != className) {
            setClassName(cn);
          }
          setCurrentImageIndex(0);
          lazyLoad(0);
        }
      }
    }
  });

  useInterval({
    cb: () => {
      const imageIndex = getNextImage(currentImageIndex, images);
      const cn = IsValidUrl(images[imageIndex]?.linkTo) ? 'click' : '';

      if (cn != className) {
        setClassName(cn);
      }
      setCurrentImageIndex(imageIndex);
      lazyLoad(imageIndex);
    },
    delay: 5500
  });

  const getNextImage = (index: number, images: any[]) => {
    if (images.length - 1 === index) {
      return 0;
    }
    return index + 1;
  };

  return (
    <>
      <StyledHeaderTop className={className} onClick={onClick}>
        {!loading &&
          images.map((item, index) => {
            return (
              <Slide key={index} className={index === currentImageIndex ? 'show-slide' : ''}>
                <img data-src={item.url} src={item.loaded ? item.url : ''} alt="" />
              </Slide>
            );
          })}
        <HeaderTopOverlay />

        <StyledHeaderContent className={data?.getNdPageContent?.ingress ? '' : 'title'}>
          <LogoWrapper>
            <LogoNybygg name="NYBYGG" alt="NYBYGG" />
          </LogoWrapper>

          <HomeH1
            dangerouslySetInnerHTML={{
              __html: data?.getNdPageContent?.title ? data?.getNdPageContent?.title : ''
            }}
          />

          <Ingress className={data?.getNdPageContent?.ingress ? '' : 'hide'}>
            <p
              className="imgdesc ingress"
              dangerouslySetInnerHTML={{
                __html: data?.getNdPageContent?.ingress ? data?.getNdPageContent?.ingress : ''
              }}
            ></p>
          </Ingress>
        </StyledHeaderContent>
      </StyledHeaderTop>
    </>
  );
};

const Column = styled.div`
  @media all and (max-width: 450px) {
    display: none;
  }
`;

const Slide = styled.div`
  transition: opacity 0.5s ease-in-out;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  position: absolute;
  width: 100%;
  opacity: 0;

  &.show-slide {
    opacity: 1;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const HeaderTopOverlay = styled.div`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.6);
  content: '';
  width: 100%;
  height: 100%;
  top: 0;
`;

export const StyledHeaderTop = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 600px;
  height: 50vh;
  margin: 0 auto;

  @media all and (min-width: 600px) {
    height: 600px;
  }

  &.click {
    cursor: pointer;
  }
`;

export const StyledHeaderImage = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 80vh;
  margin: 0 auto;
  background: url('https://images.unsplash.com/photo-1610304305062-1099c5e0a514?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1189&q=80')
    no-repeat;
  background-size: cover;
  background-position: center;
  padding: 0 30px;
`;

export const StyledHeaderImageServices = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 80vh;
  margin: 0 auto;
  background: url('https://images.unsplash.com/photo-1556740714-a8395b3bf30f?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1050&q=80')
    no-repeat;
  background-size: cover;
  background-position: center;
  padding: 0 30px;
`;

export const StyledHeaderOurCustomer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 80vh;
  margin: 0 auto;
  background: url('https://images.unsplash.com/uploads/14123892966835548e7bd/14369636?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1050&q=80')
    no-repeat;
  background-size: cover;
  background-position: center;
  padding: 0 30px;
`;

const StyledHeaderContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 30px;

  width: 100%;
  max-width: 1100px;
  text-align: center;
  height: 100%;

  .hide {
    display: none;
  }
`;

const LogoWrapper = styled.div`
  position: relative;
  @media all and (max-width: 330px) {
    top: 10px;
    max-width: 150px;
    margin: 0 auto;
  }
  @media all and (min-width: 600px) {
    top: -4em;
  }
`;

const HomeH1 = styled(H1)`
  position: relative;
  padding: 0;
  margin: 0;
`;

const Ingress = styled.div`
  margin: 0;
  position: relative;

  @media all and (min-width: 315px) and (max-width: 600px) {
    .imgdesc {
      position: relative;
      padding: 0 10px;
      margin: 1em 0 0 0;

      p {
        :first-child {
          margin-top: 0;
        }
      }
    }
  }
`;
